/**
 * Attempt to parse a potentially incomplete JSON string by closing
 * unclosed brackets, braces, and quotes. Returns the parsed object
 * or null if even the repaired string can't be parsed.
 *
 * Used during streaming: the LLM emits JSON incrementally, and we
 * want to render whatever is valid so far.
 */
export function parsePartialJson(partial: string): Record<string, unknown> | null {
  if (!partial || !partial.trim()) return null;

  let s = partial.trim();

  // Strip markdown code fences if present
  s = s.replace(/^```(?:json)?\s*/, "").replace(/\s*```$/, "");

  // Try parsing as-is first (fast path for complete JSON)
  try {
    const result = JSON.parse(s);
    return typeof result === "object" && result !== null ? result : null;
  } catch {
    // Continue to repair
  }

  // Remove trailing comma (invalid before closing bracket/brace)
  s = s.replace(/,\s*$/, "");

  // Remove incomplete key-value pair at the end:
  //   "key":    (value never started)
  //   "key": "partial   (string value incomplete)
  //   "key": {  (object value incomplete — will be closed below)
  // Remove dangling key with colon but no value
  s = s.replace(/,?\s*"[^"]*"\s*:\s*$/, "");

  // Close unclosed strings: count unescaped quotes
  let inString = false;
  let escaped = false;
  for (let i = 0; i < s.length; i++) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (s[i] === "\\") {
      escaped = true;
      continue;
    }
    if (s[i] === '"') {
      inString = !inString;
    }
  }
  if (inString) {
    s += '"';
  }

  // Remove trailing comma again after string close
  s = s.replace(/,\s*$/, "");

  // Close unclosed brackets and braces
  const stack: string[] = [];
  inString = false;
  escaped = false;
  for (let i = 0; i < s.length; i++) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (s[i] === "\\") {
      escaped = true;
      continue;
    }
    if (s[i] === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (s[i] === "{") stack.push("}");
    else if (s[i] === "[") stack.push("]");
    else if (s[i] === "}" || s[i] === "]") stack.pop();
  }

  // Append closers in reverse order
  s += stack.reverse().join("");

  try {
    const result = JSON.parse(s);
    return typeof result === "object" && result !== null ? result : null;
  } catch {
    return null;
  }
}
